import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

export const MARITAL_STATUSES = ["Single", "Married", "Divorced", "Widowed", "Separated"];
export const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"];
export const JOB_TYPES_NEW = ["Full Time", "Contractual", "Part Time", "Freelancer", "Commission Based", "Intern"];
export const EMP_STATUSES = ["Confirmed", "PIP", "Probation"];
export const RELATIONS = ["Father", "Mother", "Spouse", "Sibling", "Friend", "Child", "Other"];

export const MANDATORY_FIELDS_LIST = [
  "employee_code", "name", "designation", "company", "father_name", "gender",
  "marital_status", "permanent_address", "blood_group", "pan_number",
  "aadhaar_number", "personal_email", "date_of_joining", "date_of_birth",
  "personal_whatsapp", "email",
  // v109.9.145 — company_gmail & company_phone are now toggle-gated optional fields.
  "account_number", "ifsc_code", "job_type", "emp_status",
  "emergency_contact_name", "emergency_relation", "emergency_contact_number",
];

const FIELD_LABELS = {
  employee_code: "Employee ID", name: "Employee Name", designation: "Designation",
  company: "Company", father_name: "Father's Name", gender: "Gender",
  marital_status: "Marital Status", spouse_name: "Spouse Name",
  permanent_address: "Permanent Address", correspondence_address: "Correspondence Address",
  blood_group: "Blood Group", pan_number: "PAN Number", aadhaar_number: "Aadhaar Number",
  passport_no: "Passport No.", voter_id: "Voter ID",
  personal_email: "Personal Email", personal_whatsapp: "Personal WhatsApp",
  email: "Company Email (Login)", company_gmail: "Company Gmail",
  company_phone: "Company Contact Number",
  date_of_joining: "Date of Joining", date_of_birth: "Date of Birth",
  account_number: "Account Number", ifsc_code: "IFSC Code", bank_name: "Bank Name",
  job_type: "Job Type", emp_status: "Employment Status", probation_ends: "Probation Ends",
  emergency_contact_name: "Emergency Contact Name", emergency_relation: "Emergency Relation",
  emergency_contact_number: "Emergency Contact Number",
  photo: "Profile Photo",
};
export const fieldLabel = (key) => FIELD_LABELS[key] || key;

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
        {label}{required && <span className="text-rose-600 ml-1">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

/**
 * Renders all extended profile fields. Used by Add + Edit + View dialogs.
 *
 * Props:
 *   form: object with all fields
 *   setForm: updater function
 *   readOnly: if true, all inputs are disabled (for View dialog)
 */
export default function ExtendedEmployeeFields({ form, setForm, readOnly = false }) {
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ro = !!readOnly;
  const requiresSpouse = ["Married", "Divorced", "Widowed", "Separated"].includes(form.marital_status);
  const sameAddress = form.same_as_permanent !== false; // default true

  return (
    <>
      {/* ---- Personal Identity ---- */}
      <SectionHeader title="Personal Identity" />
      <Field label="Father's Name" required>
        <Input value={form.father_name || ""} onChange={(e) => upd("father_name", e.target.value)}
               disabled={ro} data-testid="emp-father-name" />
      </Field>
      <Field label="Marital Status" required>
        <Select value={form.marital_status || ""} onValueChange={(v) => upd("marital_status", v)} disabled={ro}>
          <SelectTrigger data-testid="emp-marital-status"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {MARITAL_STATUSES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      {requiresSpouse && (
        <Field label="Spouse Name" required>
          <Input value={form.spouse_name || ""} onChange={(e) => upd("spouse_name", e.target.value)}
                 disabled={ro} data-testid="emp-spouse-name" />
        </Field>
      )}
      <Field label="Blood Group" required>
        <Select value={form.blood_group || ""} onValueChange={(v) => upd("blood_group", v)} disabled={ro}>
          <SelectTrigger data-testid="emp-blood-group"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {BLOOD_GROUPS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Nationality">
        <Input
          value={form.nationality || ""}
          onChange={(e) => upd("nationality", e.target.value)}
          placeholder="e.g. Indian"
          disabled={ro}
          data-testid="emp-nationality"
        />
      </Field>

      {/* ---- Address ---- */}
      <div className="md:col-span-2 mt-2"><SectionHeader title="Address" inline /></div>
      <div className="md:col-span-2">
        <Field label="Permanent Address" required>
          <Input value={form.permanent_address || ""} onChange={(e) => upd("permanent_address", e.target.value)}
                 placeholder="House / Street, City, State, PIN" disabled={ro} data-testid="emp-permanent-address" />
        </Field>
      </div>
      <div className="md:col-span-2">
        <label className="inline-flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer">
          <Checkbox
            checked={sameAddress}
            onCheckedChange={(c) => upd("same_as_permanent", c === true)}
            disabled={ro}
            data-testid="emp-same-address"
          />
          Correspondence address is same as permanent address
        </label>
      </div>
      {!sameAddress && (
        <div className="md:col-span-2">
          <Field label="Correspondence Address" required>
            <Input value={form.correspondence_address || ""} onChange={(e) => upd("correspondence_address", e.target.value)}
                   placeholder="Current mailing address" disabled={ro} data-testid="emp-correspondence-address" />
          </Field>
        </div>
      )}

      {/* ---- Identity Numbers ---- */}
      <div className="md:col-span-2 mt-2"><SectionHeader title="Identity Numbers" inline /></div>
      <Field label="PAN Number" required>
        <Input value={form.pan_number || ""} onChange={(e) => upd("pan_number", e.target.value.toUpperCase())}
               placeholder="ABCDE1234F" disabled={ro} className="mono uppercase" data-testid="emp-pan" />
      </Field>
      <Field label="Aadhaar Number" required>
        <Input value={form.aadhaar_number || ""} onChange={(e) => upd("aadhaar_number", e.target.value.replace(/\D/g, ""))}
               placeholder="12-digit number" maxLength={12} disabled={ro} className="mono" data-testid="emp-aadhaar" />
      </Field>
      <Field label="Passport No (Optional)">
        <div className="flex items-center gap-2 mb-1.5">
          <Switch
            checked={!!form.enable_passport || !!form.passport_no}
            onCheckedChange={(v) => {
              upd("enable_passport", !!v);
              if (!v) upd("passport_no", "");
            }}
            disabled={ro}
            data-testid="emp-passport-toggle"
          />
          <span className="text-[10.5px] text-slate-500">Enable to record Passport No.</span>
        </div>
        <Input
          value={form.passport_no || ""}
          onChange={(e) => upd("passport_no", e.target.value.toUpperCase())}
          disabled={ro || (!form.enable_passport && !form.passport_no)}
          className={`mono uppercase ${(!form.enable_passport && !form.passport_no) ? "opacity-60" : ""}`}
          placeholder={(form.enable_passport || form.passport_no) ? "" : "Toggle on to enter"}
          data-testid="emp-passport"
        />
      </Field>
      <Field label="Voter ID (Optional)">
        <div className="flex items-center gap-2 mb-1.5">
          <Switch
            checked={!!form.enable_voter_id || !!form.voter_id}
            onCheckedChange={(v) => {
              upd("enable_voter_id", !!v);
              if (!v) upd("voter_id", "");
            }}
            disabled={ro}
            data-testid="emp-voter-toggle"
          />
          <span className="text-[10.5px] text-slate-500">Enable to record Voter ID.</span>
        </div>
        <Input
          value={form.voter_id || ""}
          onChange={(e) => upd("voter_id", e.target.value.toUpperCase())}
          disabled={ro || (!form.enable_voter_id && !form.voter_id)}
          className={`mono uppercase ${(!form.enable_voter_id && !form.voter_id) ? "opacity-60" : ""}`}
          placeholder={(form.enable_voter_id || form.voter_id) ? "" : "Toggle on to enter"}
          data-testid="emp-voter-id"
        />
      </Field>

      {/* ---- Contact ---- */}
      <div className="md:col-span-2 mt-2"><SectionHeader title="Contact" inline /></div>
      <Field label="Personal Email" required>
        <Input type="email" value={form.personal_email || ""} onChange={(e) => upd("personal_email", e.target.value)}
               disabled={ro} data-testid="emp-personal-email" />
      </Field>
      <Field label="Personal WhatsApp" required>
        <Input value={form.personal_whatsapp || ""} onChange={(e) => upd("personal_whatsapp", e.target.value)}
               placeholder="+91…" disabled={ro} data-testid="emp-personal-whatsapp" />
      </Field>
      <Field label="Company Gmail (Optional)">
        <div className="flex items-center gap-2 mb-1.5">
          <Switch
            checked={!!form.enable_company_gmail || !!form.company_gmail}
            onCheckedChange={(v) => {
              upd("enable_company_gmail", !!v);
              if (!v) upd("company_gmail", "");
            }}
            disabled={ro}
            data-testid="emp-company-gmail-toggle"
          />
          <span className="text-[10.5px] text-slate-500">Enable to record Company Gmail.</span>
        </div>
        <Input type="email"
          value={form.company_gmail || ""}
          onChange={(e) => upd("company_gmail", e.target.value)}
          disabled={ro || (!form.enable_company_gmail && !form.company_gmail)}
          className={(!form.enable_company_gmail && !form.company_gmail) ? "opacity-60" : ""}
          placeholder={(form.enable_company_gmail || form.company_gmail) ? "" : "Toggle on to enter"}
          data-testid="emp-company-gmail" />
      </Field>
      <Field label="Company Contact Number (Optional)">
        <div className="flex items-center gap-2 mb-1.5">
          <Switch
            checked={!!form.enable_company_phone || !!form.company_phone}
            onCheckedChange={(v) => {
              upd("enable_company_phone", !!v);
              if (!v) upd("company_phone", "");
            }}
            disabled={ro}
            data-testid="emp-company-phone-toggle"
          />
          <span className="text-[10.5px] text-slate-500">Enable to record Company Contact Number.</span>
        </div>
        <Input
          value={form.company_phone || ""}
          onChange={(e) => upd("company_phone", e.target.value)}
          disabled={ro || (!form.enable_company_phone && !form.company_phone)}
          className={(!form.enable_company_phone && !form.company_phone) ? "opacity-60" : ""}
          placeholder={(form.enable_company_phone || form.company_phone) ? "" : "Toggle on to enter"}
          data-testid="emp-company-phone" />
      </Field>

      {/* ---- Bank ---- */}
      <div className="md:col-span-2 mt-2"><SectionHeader title="Bank Account" inline /></div>
      <Field label="Account Number" required>
        <Input value={form.account_number || ""} onChange={(e) => upd("account_number", e.target.value.replace(/\D/g, ""))}
               disabled={ro} className="mono" data-testid="emp-account-number" />
      </Field>
      <Field label="IFSC Code" required>
        <Input value={form.ifsc_code || ""} onChange={(e) => upd("ifsc_code", e.target.value.toUpperCase())}
               placeholder="HDFC0001234" disabled={ro} className="mono uppercase" data-testid="emp-ifsc" />
      </Field>
      <Field label="Bank Name (Optional)">
        <Input value={form.bank_name || ""} onChange={(e) => upd("bank_name", e.target.value)}
               disabled={ro} data-testid="emp-bank-name" />
      </Field>

      {/* ---- Employment Status ---- */}
      <div className="md:col-span-2 mt-2"><SectionHeader title="Employment" inline /></div>
      <Field label="Job Type" required>
        <Select value={form.job_type || ""} onValueChange={(v) => upd("job_type", v)} disabled={ro}>
          <SelectTrigger data-testid="emp-job-type-new"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {JOB_TYPES_NEW.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Employment Status" required>
        <Select value={form.emp_status || ""} onValueChange={(v) => upd("emp_status", v)} disabled={ro}>
          <SelectTrigger data-testid="emp-emp-status"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {EMP_STATUSES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Probation Ends (auto-calculated)">
        <Input value={form.probation_ends || ""} disabled className="mono bg-slate-50"
               placeholder="DOJ + 6 months" data-testid="emp-probation-ends" />
      </Field>

      {/* ---- Emergency Contact ---- */}
      <div className="md:col-span-2 mt-2"><SectionHeader title="Emergency Contact" inline /></div>
      <Field label="Emergency Contact Name" required>
        <Input value={form.emergency_contact_name || ""} onChange={(e) => upd("emergency_contact_name", e.target.value)}
               disabled={ro} data-testid="emp-emergency-name" />
      </Field>
      <Field label="Relation" required>
        <Select value={form.emergency_relation || ""} onValueChange={(v) => upd("emergency_relation", v)} disabled={ro}>
          <SelectTrigger data-testid="emp-emergency-relation"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Emergency Contact Number" required>
        <Input value={form.emergency_contact_number || ""} onChange={(e) => upd("emergency_contact_number", e.target.value)}
               placeholder="+91…" disabled={ro} data-testid="emp-emergency-number" />
      </Field>
    </>
  );
}

function SectionHeader({ title, inline = false }) {
  return (
    <div className={`${inline ? "" : "md:col-span-2"}`}>
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-violet-700 font-bold border-b border-violet-200 pb-1 mb-1">
        {title}
      </div>
    </div>
  );
}
